"use client";

import { Check, Star, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

const plans = [
  {
    name: "FREE",
    price: "$0",
    period: "/month",
    description: "Perfect for exploring the platform",
    features: [
      "0 active campaigns (view only)",
      "Participate in hunts",
      "Basic analytics",
      "Email support",
      "Community access"
    ],
    buttonText: "Get Started Free",
    buttonVariant: "outline" as const,
    popular: false
  },
  {
    name: "STARTER",
    price: "$19.95",
    period: "/month",
    description: "Great for small teams and events",
    features: [
      "5 active campaigns",
      "500 monthly assets",
      "Advanced analytics",
      "Priority email support",
      "Custom branding",
      "Team collaboration"
    ],
    buttonText: "Start Free Trial",
    buttonVariant: "default" as const,
    popular: true
  },
  {
    name: "PREMIUM",
    price: "$29.95",
    period: "/month",
    description: "Best for growing businesses",
    features: [
      "Unlimited campaigns",
      "5,000 monthly assets",
      "Advanced analytics",
      "Priority support",
      "Custom domain",
      "White-label solution",
      "API access",
      "Dedicated account manager"
    ],
    buttonText: "Start Free Trial",
    buttonVariant: "default" as const,
    popular: false
  }
];

export function PricingSection() {
  return (
    <section id="pricing" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the perfect plan for your needs. All plans include unlimited locations per campaign 
            and access to our digital asset reward system.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg p-8 ${
                plan.popular 
                  ? 'ring-2 ring-blue-600 transform scale-105' 
                  : 'hover:shadow-xl transition-shadow duration-300'
              }`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center">
                    <Star className="w-4 h-4 mr-1" />
                    Most Popular
                  </div>
                </div>
              )}

              {/* Plan header */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-600">{plan.period}</span>
                </div>
                <p className="text-gray-600">{plan.description}</p>
              </div>

              {/* Features */}
              <div className="mb-8">
                <ul className="space-y-4">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <Link href="/auth/signup" className="block">
                <Button 
                  variant={plan.buttonVariant}
                  className={`w-full py-3 ${
                    plan.popular 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : plan.buttonVariant === 'outline' 
                        ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {plan.buttonText}
                </Button>
              </Link>

              {/* Additional info for paid plans */}
              {plan.name !== "FREE" && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  14-day free trial • No credit card required
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Enterprise section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
            <div className="flex items-center justify-center mb-4">
              <Zap className="w-8 h-8 text-yellow-400 mr-2" />
              <h3 className="text-2xl font-bold">Enterprise</h3>
            </div>
            
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Need more? We offer custom solutions for large organizations with advanced 
              security, compliance, and integration requirements.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-gray-900">
                Contact Sales
              </Button>
              <div className="text-sm text-gray-400">
                Custom pricing • Dedicated support • SLA guarantee
              </div>
            </div>
          </div>
        </div>

        {/* FAQ teaser */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">
            Have questions about our pricing?
          </p>
          <Button variant="link" className="text-blue-600 hover:text-blue-700">
            View Frequently Asked Questions →
          </Button>
        </div>
      </div>
    </section>
  );
}
